import { Link, useParams, Navigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import MarketingLayout from "@/components/marketing/MarketingLayout"
import { useBlogPost, useBlogPosts } from "@/lib/blog"
import { buildBlogPostingSchema } from "@/components/structured-data"
import { useBrand } from "@/hooks/use-brand"
import { LOCALE_CONFIG } from "@/config/locale-domains"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"

export default function BlogPost() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const post = useBlogPost(slug)
  const allPosts = useBlogPosts()
  const brand = useBrand()
  const domain = LOCALE_CONFIG[brand.locale]?.domain || LOCALE_CONFIG.ro.domain

  if (!post) return <Navigate to="/404" replace />

  const url = `https://${domain}/blog/${post.slug}`
  const jsonLd = buildBlogPostingSchema({
    title: post.title,
    description: post.description,
    date: post.date,
    author: post.author,
    url,
    image: post.image ? `https://${domain}${post.image}` : undefined,
  })

  const idx = allPosts.findIndex((p) => p.slug === slug)
  const prev = idx > 0 ? allPosts[idx - 1] : null
  const next = idx < allPosts.length - 1 ? allPosts[idx + 1] : null

  return (
    <MarketingLayout
      seoKey="blogPost"
      title={`${post.title} | ${brand.brand}`}
      description={post.description}
      image={post.image}
      jsonLd={jsonLd}
    >
      <article className="pt-36 pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground">
            ← {t("pages.blog.back")}
          </Link>
          <time className="block mt-6 text-xs text-muted-foreground" dateTime={post.date}>
            {post.date} · {post.author}
          </time>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-medium mt-3">{post.title}</h1>
          <p className="mt-4 text-lg text-muted-foreground">{post.description}</p>
          <div className="prose prose-neutral dark:prose-invert max-w-none mt-10 [&_h2]:font-display [&_p]:text-muted-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex justify-between gap-4 text-sm">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} className="text-muted-foreground hover:text-foreground">
                ← {prev.title}
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link to={`/blog/${next.slug}`} className="text-muted-foreground hover:text-foreground text-right">
                {next.title} →
              </Link>
            )}
          </div>

          <div className="mt-12 p-8 rounded-2xl border border-border bg-muted/20 text-center">
            <p className="font-display text-xl tracking-tight">{t("landing.ctaTitle")}</p>
            <Link to="/signup" className="inline-block mt-4">
              <Button className="rounded-full">
                {t("common.startFreeTrial")} <ArrowRight strokeWidth={1.5} className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </MarketingLayout>
  )
}
